<template>
  <div>
    <Transition v-bind="fadeInTransition">
      <div v-show="!editing">
        <Card class="px-2 w-full" pt:content:class="p-0!">
          <template #content>
            <div v-if="project" class="flex flex-row justify-between">
              <div class="flex flex-col w-fit my-auto justify-start gap-2">
                <div class="flex flex-row">
                  <h1 class="text-4xl font-bold">
                    {{ project.projectSettings.showBorrowerName ? getEntityDisplay(project.projectInfo.borrower) : project.projectInfo.projectName }}
                  </h1>
                  <Tag
                    rounded
                    :severity="getProjectStatusSeverity(project.projectInfo.projectStatus.category)"
                    class="mx-3 h-8 px-4 my-auto text-base!"
                    >{{ project.projectInfo.projectStatus.name }}</Tag
                  >
                  <IconButton v-tooltip.top="'Notes'" icon="file-edit" rounded outlined @click="displayProjectNotes"></IconButton>
                </div>

                <p v-if="project.projectSettings.showBorrowerName" class="italic -mt-2 text-xl">{{ project.projectInfo.projectName }}</p>
                <p class="italic -mt-2 text-xl">{{ project.projectInfo.projectCode }}</p>
              </div>
              <div class="flex flex-col w-fit my-auto gap-2">
                <div class="flex flex-row w-fit my-auto ml-auto gap-2">
                  <Button
                    v-if="canEdit"
                    outlined
                    rounded
                    label="Edit Project Info"
                    icon="pi pi-pencil"
                    class="h-10"
                    @click="startEditingProjectInfo"
                  ></Button>
                  <Chip
                    v-if="project.projectInfo.soldDate"
                    v-tooltip.top="'Sold Date'"
                    icon="pi pi-dollar text-sm"
                    class="h-10 font-bold dark:bg-surface-800 border-surface-400 border-1 text-sm gap-1"
                    :label="format(project.projectInfo.soldDate, 'MM/dd/yyyy')"
                  ></Chip>
                  <Chip
                    v-if="project.projectInfo.propertyType"
                    v-tooltip.top="'Property Type'"
                    icon="pi pi-home text-sm"
                    class="h-10 font-bold dark:bg-surface-800 border-surface-400 border-1 text-sm gap-1"
                    :label="project.projectInfo.propertyType.name"
                  ></Chip>
                  <Chip
                    v-if="project.projectInfo.propertyTypeEx"
                    v-tooltip.top="'Property Type 2'"
                    icon="pi pi-home text-sm"
                    class="h-10 font-bold dark:bg-surface-800 border-surface-400 border-1 text-sm gap-1"
                    :label="project.projectInfo.propertyTypeEx.name"
                  ></Chip>
                  <Chip
                    v-tooltip.top="'User Group'"
                    icon="pi pi-users text-sm"
                    class="h-10 font-bold dark:bg-surface-800 border-surface-400 border-1 text-sm gap-1"
                    :label="project.projectInfo.userGroup.name"
                  ></Chip>
                  <Chip
                    v-if="project.projectInfo.checkingAccount"
                    v-tooltip.top="'Ledger'"
                    icon="pi pi-book text-sm"
                    class="h-10 font-bold dark:bg-surface-800 border-surface-400 border-1 text-sm gap-1"
                    :label="project.projectInfo.checkingAccount.description"
                  ></Chip>
                  <Chip
                    v-if="project.projectInfo.subdivision"
                    v-tooltip.top="'Subdivision'"
                    icon="pi pi-sitemap text-sm"
                    class="h-10 font-bold dark:bg-surface-800 border-surface-400 border-1 text-sm gap-1"
                    :label="project.projectInfo.subdivision.subdivisionName"
                  ></Chip>
                </div>
                <div
                  v-if="
                    project.projectInfo.address.length > 0 ||
                    project.projectInfo.city.length > 0 ||
                    project.projectInfo.zip ||
                    project.projectInfo.state
                  "
                >
                  <div class="flex flex-row text-right w-fit mr-1 gap-3 ml-auto items-center">
                    <IconButton
                      v-show="project.projectInfo.address.length > 0 && project.projectInfo.city.length > 0"
                      icon="map-marker"
                      rounded
                      outlined
                      @click="launchMap"
                    ></IconButton>
                    <p class="w-auto">
                      <span v-if="project.projectInfo.address.length > 0">{{ project.projectInfo.address }}<br /></span>
                      <span v-if="project.projectInfo.city.length > 0">{{ project.projectInfo.city }}, </span>
                      <span v-if="project.projectInfo.state && project.projectInfo.state.length > 0">{{ project.projectInfo.state + " " }}</span>
                      <span v-if="project.projectInfo.zip && project.projectInfo.zip.length > 0">{{ project.projectInfo.zip }}</span>
                    </p>
                  </div>
                </div>
                <p v-else class="text-right mr-1">No Address Provided</p>
              </div>
            </div>
          </template>
        </Card>
        <div class="flex flex-row gap-3 mt-3">
          <ProjectEntitiesDisplay :project="project"></ProjectEntitiesDisplay>
          <Card class="w-[500px] flex-none h-auto" pt:body:class="h-full" pt:content:class="h-full">
            <template #title>Budget Categories</template>
            <template #content>
              <div class="h-full flex flex-col justify-center">
                <PieChart ref="statusChart" :info="statusInfo"></PieChart>
              </div>
            </template>
          </Card>
          <Card class="w-full h-auto" pt:content:class="h-full" pt:body:class="h-full">
            <template #title>Disbursement History</template>
            <template #content>
              <div class="h-full flex flex-col justify-center">
                <LineChart ref="disbursementChart" :info="disbursementHistoryInfo"></LineChart>
              </div>
            </template>
          </Card>
        </div>
      </div>
    </Transition>

    <div class="flex flex-row gap-3 mt-3">
      <ProjectContactsManager v-if="!editing" v-model:contacts="contacts" :can-edit :source-of-funds :project="project"> </ProjectContactsManager>
      <FlexFieldsDisplay
        v-if="!editing || (editing && editingType === 'Fields')"
        v-model:currentFlexFields="currentFlexFields"
        v-model:editing="editing"
        v-model:editing-title="editingTitle"
        :can-edit
        :project-id="project.projectInfo.projectId"
        :draw-id="project.projectDrawInfo.drawId"
        class="w-full"
        @start-editing="editingType = 'Fields'"
      ></FlexFieldsDisplay>
    </div>

    <!-- <Transition v-bind="pushUpTransition"> -->
    <div v-if="editing && project && editingType === 'Info'">
      <Card>
        <template #content>
          <EditProjectInfo
            v-model:project="project"
            :source-of-funds
            :contacts
            :permissions
            @cancel="stopEditingProjectInfo"
            @submit="submitProjectInfoChanges"
          ></EditProjectInfo>
        </template>
      </Card>
    </div>
    <!-- </Transition> -->

    <TSDialog v-model:visible="showProjectNotes" :label="(canEdit ? 'Edit' : 'Display') + ' Project Notes'" icon="file-edit" size="h-fit w-[800px]!">
      <template #content>
        <p class="font-bold">General Project Information</p>
        <Textarea v-model="projectNotes" auto-resize :disabled="!canEdit" rows="10" class="w-full"></Textarea>
        <p class="font-bold mt-3">Collateral Notes</p>
        <Textarea v-model="specialInstructions" auto-resize :disabled="!canEdit" rows="10" class="w-full"></Textarea>
        <div class="flex justify-end mt-3">
          <SubmitButton v-if="canEdit" @click="submitProjectNotes"></SubmitButton>
        </div>
      </template>
    </TSDialog>
  </div>
</template>

<script setup lang="ts">
import { getProjectStatusSeverity } from "@/enums/ProjectStatusCategory";
import type { Project, ProjectInfo } from "@/types/Projects";
import type { DrawItem } from "@/types/DrawItem";
import { getEntityDisplay, openMap } from "@/services/Utility";
import { z } from "zod";
import { type DisbursementHistory, DisbursementHistoryListSchema } from "@/types/DisbursementHistory";
import type { ProjectEntity } from "@/types/ProjectEntity";
import type { FlexRowField } from "@/types/FlexRows";
import type { UserPermissions } from "@/types/User";
import { fadeInTransition } from "@/services/Transitions";
import { format } from "date-fns";
import { LineChartData, LineChartInfo, PieChartInfo } from "@/classes/ChartInfo";

const project = defineModel<Project>("project", { required: true });
const drawItems = defineModel<DrawItem[]>("drawItems", { required: true });
const contacts = defineModel<ProjectEntity[]>("contacts", { required: true });
const currentFlexFields = defineModel<FlexRowField[]>("currentFlexFields", { required: true });
const editing = defineModel<boolean>("editing", { required: true });
const editingTitle = defineModel<string>("editingTitle", { required: true });

const disbursementHistory = ref<DisbursementHistory[]>([]);

const statusChart = ref();
const disbursementChart = ref();

let projectHistory = useManualRefHistory(project, { clone: true });
let projectInfoSubmitted = false;

const editingType = ref<"Info" | "Fields">();
const showProjectNotes = ref(false);
const projectNotes = ref("");
const specialInstructions = ref("");

interface Props {
  canEdit: boolean;
  sourceOfFunds: ProjectEntity[];
  permissions: UserPermissions;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  projectInfoUpdated: [];
}>();

const disbursementHistoryInfo = ref<LineChartInfo>(new LineChartInfo());
disbursementHistoryInfo.value.height = "h-[500px]";
disbursementHistoryInfo.value.currency = true;

const statusInfo = ref<PieChartInfo>(new PieChartInfo());
statusInfo.value.donut = true;
statusInfo.value.title = "Total";
statusInfo.value.currency = true;
statusInfo.value.height = "h-[450px]";
statusInfo.value.titleTopPosition = "35%";

const launchMap = (): void => {
  if (project.value == null || project.value.projectInfo.zip == null) return;
  openMap(project.value.projectInfo.address, project.value.projectInfo.city, project.value.projectInfo.zip);
};

const updateDrawItemTypeData = (): void => {
  const categories: string[] = [...new Set(drawItems.value.map((item: DrawItem) => item.custCostType))];
  const totals: number[] = [];
  categories.forEach((custCostType: string) => {
    const budgetAmount: number = drawItems.value
      .filter((item: DrawItem) => item.custCostType === custCostType)
      .reduce((accumulator: number, item: DrawItem) => accumulator + item.adjustedBudget, 0);
    totals.push(budgetAmount);
  });

  statusInfo.value.labels = categories;
  statusInfo.value.data = totals;
  statusChart.value.loadingComplete();
};

const startEditingProjectInfo = (): void => {
  editingType.value = "Info";
  projectHistory = useManualRefHistory(project, { clone: true });
  projectHistory.commit();
  editing.value = true;
  editingTitle.value = "Editing Project Info";
};

const submitProjectInfoChanges = async (values: ProjectInfo): Promise<void> => {
  await axios
    .post("/projects/update-info/" + project.value.projectDrawInfo.drawNumber, {
      projectInfo: values,
      projectSettings: project.value.projectSettings,
    })
    .then(() => emit("projectInfoUpdated"))
    .catch((e) => errorApi(e));

  projectHistory.commit();
  projectInfoSubmitted = true;
  stopEditingProjectInfo();
};

const stopEditingProjectInfo = (): void => {
  if (!projectInfoSubmitted) {
    projectHistory.undo();

    //The undo function changes the format of the date to a string and messes up the display
    setTimeout(() => {
      project.value.projectDrawInfo.dateCreated = z.coerce.date().parse(project.value.projectDrawInfo.dateCreated);
      project.value.projectDrawInfo.completionDate = z.coerce.date().nullable().parse(project.value.projectDrawInfo.completionDate);
      project.value.projectDrawInfo.inspectionDate = z.coerce.date().nullable().parse(project.value.projectDrawInfo.inspectionDate);
    }, 10);
  }
  projectInfoSubmitted = false;
  editing.value = false;
  editingTitle.value = "";
};

const displayProjectNotes = (): void => {
  projectNotes.value = project.value.projectInfo.projectNotes;
  specialInstructions.value = project.value.projectInfo.specialInstructions;
  showProjectNotes.value = true;
};

const submitProjectNotes = (): void => {
  axios
    .post("/projects/notes/update/" + project.value.projectInfo.projectId, {
      projectNotes: projectNotes.value,
      specialInstructions: specialInstructions.value,
    })
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          project.value.projectInfo.projectNotes = projectNotes.value;
          project.value.projectInfo.specialInstructions = specialInstructions.value;
          showProjectNotes.value = false;
        },
        (errors: ApiError[]) => {
          error("Error Occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const getDisbursementHistoryData = (): void => {
  axios
    .get("/projects/payment-data?projectId=" + project.value.projectInfo.projectId)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          if (!parseData(DisbursementHistoryListSchema, data, disbursementHistory)) return;
          const labels: string[] = [];
          const loanAdvances: LineChartData = new LineChartData();
          loanAdvances.name = "Loan Advances";
          const equityDisbursements: LineChartData = new LineChartData();
          equityDisbursements.name = "Equity Disbursements";
          disbursementHistory.value.forEach((ph: DisbursementHistory) => {
            labels.push(ph.month);
            loanAdvances.data.push(ph.loanAdvances);
            equityDisbursements.data.push(ph.equityDisbursements);
          });

          disbursementHistoryInfo.value.labels = labels;
          disbursementHistoryInfo.value.lines.push(loanAdvances);
          disbursementHistoryInfo.value.lines.push(equityDisbursements);

          disbursementChart.value.loadingComplete();
        },
        (errors: ApiError[]) => {
          error("Error Occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

onMounted(async (): Promise<void> => {
  updateDrawItemTypeData();
  getDisbursementHistoryData();
});

defineExpose({ getDisbursementHistoryData });
</script>
